export type TFitKey = "to" | "regular" | "relaxed";
export type TGender = "M" | "F";

export interface ISizeCell {
  to: string;
  regular: string;
  relaxed: string;
}

export interface IGenderBands {
  height_bands: [number, number][];
  weight_bands: [number, number][];
}

export interface ISizeGuide {
  version: string;
  bands: Record<TGender, IGenderBands>;
  // categories[gender][category][heightBand][weightBand]
  categories: Record<TGender, Record<string, Record<string, Record<string, ISizeCell>>>>;
}

// Shopify product_type → 5개 대표 그룹.
// 실제 등록된 product_type 15개 기준. 키는 소문자로 정규화해 비교
const PRODUCT_TYPE_TO_CATEGORY: Record<string, string> = {
  outerwear: "Outer",

  shirts: "Tops",
  knits: "Tops",
  tops: "Tops",

  "t-shirts": "Tee",

  sweatshirts: "Sweatshirts",

  pants: "Pants_Shorts",
  shorts: "Pants_Shorts",
};

// 사이즈 추천 대상이 아닌 product_type. 이 상품에는 추천 UI 자체를 노출하지 않음
const UNSUPPORTED_PRODUCT_TYPES: ReadonlySet<string> = new Set([
  "accessory",
  "bags",
  "gwp",
  "headwear",
  "shoes",
  "socks",
  "underwear",
]);

const SIZE_GUIDE_URL = "https://intl.thisisneverthat.com/data/size-guide.json";

export async function loadSizeGuide(): Promise<ISizeGuide> {
  const res = await fetch(SIZE_GUIDE_URL);
  if (!res.ok) throw new Error(`size guide fetch failed: ${res.status}`);
  return (await res.json()) as ISizeGuide;
}

export function resolveCategory(productType: string): string | null {
  const key = productType.trim().toLowerCase();
  return PRODUCT_TYPE_TO_CATEGORY[key] ?? null;
}

/**
 * 이 상품에 사이즈 추천 UI를 노출할지 여부.
 * 매핑에 없는 값(액세서리/신발/양말 등, 또는 신규 미등록 타입)은 false → UI 자체를 숨김
 */
export function isSizeRecommendationSupported(productType: string): boolean {
  const key = productType.trim().toLowerCase();
  if (UNSUPPORTED_PRODUCT_TYPES.has(key)) return false;
  return key in PRODUCT_TYPE_TO_CATEGORY;
}

/**
 * 고객 메타필드의 성별 값을 M/F로 정규화.
 * 상품에는 성별이 없고 고객 정보에만 있으므로, 로그인 고객은 이 값을 기본값으로 사용
 * 비로그인·미입력·이상값이면 null → UI에서 직접 받아야 함
 */
export function normalizeGender(raw: string | null | undefined): TGender | null {
  if (!raw) return null;
  const v = raw.trim().toLowerCase();
  if (["m", "male", "men", "man", "남", "남성"].includes(v)) return "M";
  if (["f", "female", "women", "woman", "여", "여성"].includes(v)) return "F";
  return null; // "기타", 미입력 등 → 폴백
}

/**
 * 내부 표기(M/F) → 메타필드 저장 표기(Male/Female).
 * 마이페이지가 info.gender를 "Male"/"Female"로 저장하므로 저장 직전 이 값으로 변환
 */
export function toStoredGender(g: TGender): "Male" | "Female" {
  return g === "M" ? "Male" : "Female";
}

// 숫자값이 [min, max) 구간 중 어디에 드는지 찾아 "min-max" 키를 반환.
function findBand(value: number, bands: [number, number][]): string | null {
  for (const [lo, hi] of bands) {
    if (value >= lo && value < hi) return `${lo}-${hi}`;
  }
  // 경계 밖이면 가장 가까운 구간으로 클램프(범위 밖 입력 방어)
  const first = bands[0],
    last = bands[bands.length - 1];
  if (value < first[0]) return `${first[0]}-${first[1]}`;
  if (value >= last[1]) return `${last[0]}-${last[1]}`;
  return null;
}

export interface IRecommendInput {
  gender: TGender;
  productType: string;
  heightCm: number;
  weightKg: number;
}

export interface IRecommendResult {
  gender: TGender;
  category: string;
  heightBand: string;
  weightBand: string;
  sizes: ISizeCell; // { to, regular, relaxed }
}

// 사이즈 근접도 계산 및 정렬 기준. 이 목록에 없는 표기는 거리 계산에서 제외됨
const SIZE_ORDER = ["XS", "XS", "S", "M", "L", "XL", "XXL"];

// size-guide 데이터의 사이즈 셀은 "XS or S"처럼 후보가 여러 개 붙어 올 수 있음
export function parseSizeCandidates(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/\s+or\s+/i)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

/**
 * 추천 후보 중 이 상품이 실제로 갖고 있는 사이즈를 순서대로 찾음
 * 후보가 하나도 없으면(대부분 XS) 첫 번째 후보 기준 사이즈 순서상 가장 가까운 보유 사이즈로 대체
 */
export function resolveAvailableSize(raw: string, availableSizes: string[]): string | null {
  if (!availableSizes.length) return null;

  const candidates = parseSizeCandidates(raw);
  const exact = candidates.find((size) => availableSizes.includes(size));
  if (exact) return exact;

  const primaryIndex = SIZE_ORDER.indexOf(candidates[0]);
  if (primaryIndex === -1) return availableSizes[0]; // 정렬 기준에 없는 표기 → 임의 폴백

  let closest: string | null = null;
  let closestDistance = Infinity;
  for (const size of availableSizes) {
    const index = SIZE_ORDER.indexOf(size);
    if (index === -1) continue;

    const distance = Math.abs(index - primaryIndex);
    if (distance < closestDistance) {
      closestDistance = distance;
      closest = size;
    }
  }

  return closest ?? availableSizes[0];
}

export function recommend(guide: ISizeGuide, input: IRecommendInput): IRecommendResult | null {
  const { gender } = input;
  const category = resolveCategory(input.productType);
  if (!category) return null;

  const genderCats = guide.categories[gender];
  const genderBands = guide.bands[gender];
  if (!genderCats || !genderBands || !genderCats[category]) return null;

  const hb = findBand(input.heightCm, genderBands.height_bands);
  const wb = findBand(input.weightKg, genderBands.weight_bands);
  if (!hb || !wb) return null;

  const cell = genderCats[category]?.[hb]?.[wb];
  if (!cell) return null;

  return { gender, category, heightBand: hb, weightBand: wb, sizes: cell };
}
